import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { isDesktopExperience, PREVIEW_DESKTOP_EVENT } from "@/lib/pwa";
import {
  loadFavorites, getFavoritesCache, FAVORITES_EVENT, requestFavNav, setFavoritesOrder,
} from "@/lib/favorites";
import { favMeta } from "@/lib/officeFavorites";
import { Home, CalendarDays, CalendarOff, Archive, Clock, GripVertical } from "lucide-react";

// Link "Home" fissi per ruolo (rotte esistenti). I Preferiti dell'Ufficio sono dinamici (sotto).
const HOME = {
  LAVORATORE: [
    { label: "Bacheca", to: "/lavoratore", icon: Home },
    { label: "Agenda", to: "/agenda", icon: CalendarDays },
    { label: "Assenze", to: "/assenze", icon: CalendarOff },
    { label: "Ore indirette", to: "/ore-indirette", icon: Clock },
    { label: "Archivio", to: "/archivio", icon: Archive },
  ],
  UFFICIO: [{ label: "Home Ufficio", to: "/ufficio", icon: Home }],
  AMMINISTRATORE: [{ label: "Amministrazione", to: "/admin", icon: Home }],
};

// Card compatta di un Preferito Ufficio: mantiene l'identità visiva (colore + icona) della card originale.
function FavCard({ fav, navigate, index, dnd }) {
  const meta = favMeta(fav.section);
  if (!meta) return null;
  const Icon = meta.icon;
  const isComm = meta.variant === "comm";
  const cardCls = isComm ? "bg-[#CDC4E8]" : "bg-white";
  const boxCls = isComm ? "bg-[#3A3A4C] border border-[#2C2C39]" : "bg-gradient-to-br from-[#E1DEEE] to-[#CFC9E2] border border-[#BCB6D2]";
  const iconCls = isComm ? "text-white" : "text-primary";
  const dragging = dnd.dragIndex === index;
  const showInsert = dnd.overIndex === index && dnd.dragIndex !== null && dnd.dragIndex !== index;
  return (
    <div
      draggable
      onDragStart={(e) => { dnd.onDragStart(index); try { e.dataTransfer.effectAllowed = "move"; } catch (_) {} }}
      onDragOver={(e) => { e.preventDefault(); dnd.onDragOver(index); }}
      onDrop={(e) => { e.preventDefault(); dnd.onDrop(index); }}
      onDragEnd={dnd.onDragEnd}
      className={`relative transition-opacity ${dragging ? "opacity-40" : "opacity-100"}`}>
      {showInsert && (
        <span data-testid="desktop-fav-insert" className="absolute -top-1 left-1 right-1 h-0.5 rounded-full bg-amber-400" />
      )}
      <button
        data-testid={`desktop-fav-${fav.group}-${fav.section}`}
        onClick={() => { if (dnd.dragIndex !== null) return; navigate("/ufficio"); requestFavNav(fav.group, fav.section); }}
        className={`w-full flex items-center gap-1.5 rounded-lg ${cardCls} pl-1 pr-2 py-2 text-left shadow-sm hover:shadow-md active:scale-[0.98] transition-all duration-200`}>
        <GripVertical className={`h-3.5 w-3.5 shrink-0 cursor-grab active:cursor-grabbing ${isComm ? "text-slate-600" : "text-slate-400"}`} strokeWidth={2} />
        <span className={`h-8 w-8 rounded-lg ${boxCls} shadow-inner flex items-center justify-center shrink-0`}>
          <Icon className={`h-4 w-4 ${iconCls}`} strokeWidth={2} />
        </span>
        <span className="text-[13px] font-medium text-slate-900 leading-tight truncate">{meta.label}</span>
      </button>
    </div>
  );
}

export function DesktopSidebar() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(() => isDesktopExperience());
  const [favs, setFavs] = useState(() => getFavoritesCache());
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  useEffect(() => {
    const refresh = () => setActive(isDesktopExperience());
    window.addEventListener("appinstalled", refresh);
    window.addEventListener(PREVIEW_DESKTOP_EVENT, refresh);
    let mq;
    try { mq = window.matchMedia("(display-mode: standalone)"); mq.addEventListener("change", refresh); } catch (_) {}
    return () => {
      window.removeEventListener("appinstalled", refresh);
      window.removeEventListener(PREVIEW_DESKTOP_EVENT, refresh);
      try { mq && mq.removeEventListener("change", refresh); } catch (_) {}
    };
  }, []);

  // Carica/aggiorna i Preferiti dell'account (ricostruiti al login e ad ogni modifica della stella).
  useEffect(() => {
    if (active && user) loadFavorites(true).then(setFavs);
    const h = () => setFavs(getFavoritesCache());
    window.addEventListener(FAVORITES_EVENT, h);
    return () => window.removeEventListener(FAVORITES_EVENT, h);
  }, [active, user?.id]);

  // Sincronizza il padding-sinistra del contenuto con la reale visibilità della barra (transizione elegante via CSS).
  useEffect(() => {
    const role = user?.profilo_accesso;
    const showFavs = role === "UFFICIO" || role === "AMMINISTRATORE";
    const hasContent = showFavs ? favs.length > 0 : ((HOME[role] || []).length > 0);
    const open = !!(active && user && hasContent);
    document.documentElement.classList.toggle("orbit-sidebar-open", open);
  }, [active, user, favs]);
  useEffect(() => () => document.documentElement.classList.remove("orbit-sidebar-open"), []);

  // Solo in esperienza desktop (app installata REALE o override di sviluppo in Preview) e con utente autenticato.
  if (!active || !user) return null;
  const role = user.profilo_accesso;
  const homeItems = HOME[role] || [];
  const accountName = [user.nome, user.cognome].filter(Boolean).join(" ") || user.nome || "Account";
  const showFavs = role === "UFFICIO" || role === "AMMINISTRATORE";
  // Barra visibile solo se c'è contenuto: Preferiti (Ufficio/Admin) o voci fisse (Lavoratore). Altrimenti scivola via.
  const visible = showFavs ? favs.length > 0 : homeItems.length > 0;

  const dnd = {
    dragIndex,
    overIndex,
    onDragStart: (i) => { setDragIndex(i); setOverIndex(i); },
    onDragOver: (i) => setOverIndex(i),
    onDragEnd: () => { setDragIndex(null); setOverIndex(null); },
    onDrop: (i) => {
      const from = dragIndex;
      setDragIndex(null); setOverIndex(null);
      if (from === null || from === i) return;
      const next = [...favs];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      setFavs(next);
      setFavoritesOrder(next);
    },
  };

  return (
    <aside data-testid="desktop-sidebar" aria-hidden={!visible}
      className={`fixed left-0 top-0 bottom-0 w-[236px] z-40 bg-[#23232B] text-slate-200 flex flex-col px-3 py-5 select-none overflow-y-auto transition-transform duration-[320ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${visible ? "translate-x-0" : "-translate-x-full pointer-events-none"}`}>
      <div className="px-2 mb-5">
        <p className="text-sm font-heading font-semibold text-white truncate" data-testid="desktop-sidebar-account">{accountName}</p>
        <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-0.5">Preferiti</p>
      </div>
      {!showFavs && (
        <nav className="flex flex-col gap-1">
          {homeItems.map((it) => {
            const isActive = location.pathname === it.to || location.pathname.startsWith(it.to + "/");
            const Icon = it.icon;
            return (
              <button key={it.to} data-testid={`desktop-fav-${it.to.replace(/\//g, "")}`}
                onClick={() => navigate(it.to)}
                className={`w-full flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                  isActive ? "bg-white/12 text-white font-semibold" : "text-slate-300 hover:bg-white/6 hover:text-white"
                }`}>
                <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                <span className="truncate">{it.label}</span>
              </button>
            );
          })}
        </nav>
      )}

      {showFavs && favs.length > 0 && (
        <div className="flex flex-col gap-2" data-testid="desktop-fav-list">
          {favs.map((f, i) => <FavCard key={`${f.group}:${f.section}`} fav={f} navigate={navigate} index={i} dnd={dnd} />)}
        </div>
      )}
    </aside>
  );
}

export default DesktopSidebar;
